<script setup lang="ts">
import { useTranslation } from "i18next-vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import { getObservationKindIcon } from "@/components/field-observations/observation-type";
import type { AnalysisGroupKind } from "@/utils/plan-analysis/types";

/**
 * Type d'observation : désordre, vue d'ensemble ou commentaire — trois
 * pastilles, les mêmes sur la carte d'un groupe à valider et sur celle d'une
 * observation. Pour un groupe, la nature proposée est dérivée de ses membres
 * (cf. `deriveGroupKind`) ; l'inspecteur la corrige ici en un tap. La
 * pastille active suit `--card-accent` (violet par défaut — analyse).
 */

defineProps<{
  modelValue: AnalysisGroupKind;
}>();

defineEmits<{
  (e: "update:modelValue", kind: AnalysisGroupKind): void;
}>();

const { t } = useTranslation();

const KINDS: AnalysisGroupKind[] = ["disorder", "overview", "comment"];
</script>

<template>
  <div class="kind-group d-flex" @click.stop>
    <button
      v-for="kind in KINDS"
      :key="kind"
      type="button"
      class="kind-btn d-flex align-items-center justify-content-center gap-1"
      :class="{ active: modelValue === kind }"
      @click="$emit('update:modelValue', kind)"
    >
      <PhosphorIcon :name="getObservationKindIcon(kind)" :size="13" />
      {{ t(`canvas:analysis.kinds.${kind}`) }}
    </button>
  </div>
</template>

<style lang="scss" scoped>
.kind-group {
  background: rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  padding: 2px;
  gap: 2px;
  // Largeur de référence de la règle ci-dessous (icônes masquées si étroit).
  container-type: inline-size;
}

// Trois libellés + trois icônes ne tiennent pas dans le volet à sa largeur
// par défaut : la rangée débordait de la carte. En dessous du seuil, les
// icônes cèdent la place — les libellés restent entiers, sur une ligne.
@container (max-width: 285px) {
  .kind-btn svg {
    display: none;
  }
}

.kind-btn {
  flex: 1;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: inherit;
  opacity: 0.7;
  font-size: 0.74rem;
  font-weight: 600;
  padding: 4px 6px;
  cursor: pointer;
  white-space: nowrap;

  &.active {
    background-color: var(--card-accent, var(--dr-report));
    color: white;
    opacity: 1;
  }
}
</style>
